import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Trash2, ChevronLeft } from "lucide-react";

export default function Cart() {
  const nav = useNavigate();
  const { cart, updateQty, removeItem, format, cartSubtotalEur, t } = useApp();

  if (cart.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="text-3xl font-display font-bold mb-4">{t("cart.empty")}</h1>
        <p className="text-muted-foreground mb-8">{t("cart.emptyText")}</p>
        <Button asChild size="lg" className="rounded-full btn-primary" data-testid="cart-continue-shopping">
          <Link to="/produtos">{t("cta.viewProducts")}</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-12">
      <Link to="/produtos" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ChevronLeft className="w-4 h-4" /> {t("cta.continue")}
      </Link>
      <h1 className="text-4xl font-display font-bold mb-8">{t("cart.title")}</h1>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-3">
          {cart.map((i) => (
            <div key={i.key} className="flex gap-4 p-4 rounded-2xl border border-border bg-card" data-testid={`cart-item-${i.slug}`}>
              <img src={i.image} alt={i.name} className="w-24 h-24 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                <Link to={`/produto/${i.slug}`} className="font-display font-semibold hover:text-primary">{i.name}</Link>
                {(i.color || i.size) && (
                  <div className="text-xs text-muted-foreground mt-1">{i.color}{i.color && i.size ? " • " : ""}{i.size}</div>
                )}
                <div className="mt-2 flex items-center gap-3">
                  <div className="flex items-center border border-border rounded-full text-sm">
                    <button onClick={() => updateQty(i.key, i.quantity - 1)} className="px-3 py-1" data-testid={`qty-minus-${i.slug}`}>-</button>
                    <span className="px-3 font-mono">{i.quantity}</span>
                    <button onClick={() => updateQty(i.key, i.quantity + 1)} className="px-3 py-1" data-testid={`qty-plus-${i.slug}`}>+</button>
                  </div>
                  <button onClick={() => removeItem(i.key)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-${i.slug}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="font-display font-bold">{format(i.unit_price_eur * i.quantity)}</div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-border p-6 h-fit bg-card space-y-4">
          <h3 className="font-display font-semibold text-lg">{t("cart.summary")}</h3>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t("cart.subtotal")}</span>
            <span className="font-medium">{format(cartSubtotalEur)}</span>
          </div>
          <div className="text-xs text-muted-foreground">{t("cart.taxesNote")}</div>
          <Button size="lg" className="w-full rounded-full btn-primary" onClick={() => nav("/checkout")} data-testid="cart-checkout-btn">
            {t("cta.checkout")}
          </Button>
        </div>
      </div>
    </div>
  );
}
